import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared className for profile sheet header nav (prev / next / close) icon
 * buttons. Replaces the duplicated PROFILE_NAV_ICON_BTN string-constant
 * usage in jobs/pipeline/profile.tsx and search/profile/index.tsx.
 *
 * Originates from Figma 799:1348 / 799:1355 (profile sheet nav + close icon
 * buttons). Re-exported as a deprecated back-compat constant from
 * `features/candidate/lib/profile-constants.ts`; a follow-up PR removes the
 * constant entirely. Prefer `<ProfileNavIconButton>` for all new call sites.
 */
export const profileNavIconButtonClassName =
  "flex shrink-0 cursor-pointer items-center justify-center overflow-clip rounded-[5.25px] bg-contrast/8 p-0 min-h-7 min-w-7 transition-colors hover:bg-contrast/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-contrast/20 disabled:cursor-not-allowed disabled:opacity-40";

export type ProfileNavIconButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Profile sheet nav / close icon button. Encapsulates the shared className
 * surface previously inlined via the PROFILE_NAV_ICON_BTN string constant.
 *
 * Pass any standard button props (type, onClick, disabled, aria-label,
 * className). Additional className strings compose with the shared base via
 * `cn`. The default `type` is `button` so the component never accidentally
 * submits forms when used inside one.
 */
export const ProfileNavIconButton = forwardRef<
  HTMLButtonElement,
  ProfileNavIconButtonProps
>(function ProfileNavIconButton(
  { className, type = "button", children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(profileNavIconButtonClassName, className)}
      {...rest}
    >
      {children}
    </button>
  );
});
